import { useEffect, useRef, useState } from 'react'
import { Badge, Button, Card, Col, Row, Space, Tag, Typography } from 'antd'
import {
  ArrowRightOutlined,
  CheckCircleFilled,
  DashboardOutlined,
  DollarCircleOutlined,
  DotChartOutlined,
  ExperimentOutlined,
  GithubOutlined,
  LinkedinOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
  ShopOutlined,
  ThunderboltOutlined,
  TwitterOutlined,
} from '@ant-design/icons'
import { Link } from 'react-router-dom'

type SellerLandingPageProps = {
  initialSection?: 'pricing'
}

type FeatureItem = {
  title: string
  description: string
  icon: React.ReactNode
}

type PlanItem = {
  code: string
  name: string
  subtitle: string
  monthlyPrice: number
  yearlyPrice: number
  inventoryLimit: string
  support: string
  payout: string
  bestFor: string
  cta: string
  highlights: string[]
  popular?: boolean
}

type IllustrationSlot = {
  title: string
  caption: string
  suggestedPath: string
}

type StoryPanel = {
  title: string
  eyebrow: string
  description: string
  bullets: string[]
  imagePath: string
}

const heroStats = [
  { label: 'Plan catalog', value: '3 tiers', hint: 'Starter, Business, Enterprise with monthly and yearly billing' },
  { label: 'Team access model', value: 'OTP + RBAC', hint: 'Staff login via OTP with role and permission controls' },
  { label: 'Seller operations', value: '10+ modules', hint: 'Dashboard, products, inventory, orders, heatmap, support, wallet, and settings' },
]

const featureItems: FeatureItem[] = [
  {
    title: 'Analytics-ready Seller Dashboard',
    description: 'Track totals, today stats, revenue trend, order trend, order-status split, inventory distribution, and top category sales with date filtering.',
    icon: <DashboardOutlined />,
  },
  {
    title: 'Inventory + Geospatial Demand View',
    description: 'Manage SKU stock, low-stock checks, category inventory analysis, and order heatmap with city/pincode filtering and nearby-order drilldown.',
    icon: <DotChartOutlined />,
  },
  {
    title: 'Role-safe Team Operations',
    description: 'Create roles, assign fine-grained permissions, onboard staff users, and enforce access by both permission checks and active plan features.',
    icon: <SafetyCertificateOutlined />,
  },
  {
    title: 'Billing, Razorpay, and Wallet Controls',
    description: 'Activate or renew plans through Razorpay, manage billing records and payment methods, request payouts, and export wallet transaction CSVs.',
    icon: <DollarCircleOutlined />,
  },
]

const painPoints = [
  {
    pain: 'Approval and access confusion after registration.',
    solution: 'Trendify uses clear onboarding stages: admin approval, login, and plan activation. Sellers without an active plan are routed to activation before operations.',
  },
  {
    pain: 'Stock and order operations split across multiple tools.',
    solution: 'Products, inventory, order status updates, delivered-order CSV export, and demand heatmap are available in the same seller workspace.',
  },
  {
    pain: 'Teams share credentials or get over-broad access.',
    solution: 'Staff sign-in uses OTP, while roles and permissions restrict every section from dashboard to support and wallet actions.',
  },
  {
    pain: 'Support and payout workflows lack visibility.',
    solution: 'Ticket queues with threaded replies plus wallet summaries, payout requests, and fee-aware transaction history keep operations transparent.',
  },
]

const pricingPlans: PlanItem[] = [
  {
    code: 'starter',
    name: 'Starter',
    subtitle: 'For new compliant sellers',
    monthlyPrice: 1999,
    yearlyPrice: 19999,
    inventoryLimit: 'Up to 5 products',
    support: 'Ticket support (24x5)',
    payout: 'T+4D payout',
    bestFor: 'Early-stage sellers building catalog and basic operations',
    cta: 'Start With Starter',
    highlights: [
      'Up to 10 products',
      'Up to 3 staff members',
      'Inventory up to 5 products',
      'Ticket support (24x5)',
      'T+4D payout',
    ],
  },
  {
    code: 'business',
    name: 'Business',
    subtitle: 'For active multi-user operations',
    monthlyPrice: 4999,
    yearlyPrice: 49999,
    inventoryLimit: 'Full inventory access',
    support: '24x7 support (2-hour)',
    payout: 'T+2D payout',
    bestFor: 'Growing teams needing analytics, custom roles, and faster payouts',
    cta: 'Choose Business',
    highlights: [
      'Up to 25 products',
      'Up to 10 staff members',
      'Full catalog & inventory tools',
      'Instant order updates',
      'Analytics dashboard access',
      'Custom roles & permissions',
      'Priority support (24x7)',
      'T+2D payout',
    ],
    popular: true,
  },
  {
    code: 'enterprise',
    name: 'Enterprise',
    subtitle: 'For high-volume sellers',
    monthlyPrice: 8999,
    yearlyPrice: 89999,
    inventoryLimit: 'Full inventory access',
    support: '24x7 highest priority',
    payout: 'T+5H payout',
    bestFor: 'Scale-focused sellers needing advanced analytics and fastest payout lane',
    cta: 'Choose Enterprise',
    highlights: [
      'Up to 50 products',
      'Up to 25 staff members',
      'Full catalog & inventory tools',
      'Full instant order workflow',
      'Advanced analytics',
      'Custom roles & permissions',
      'Highest priority support',
      'Instant payout (T+5H)',
      'Fast product approval',
      'Personalized analytics',
      'Exclusive Warehouse and Order Delivery features in 48 hours',
    ],
  },
]

const storyPanels: StoryPanel[] = [
  {
    title: 'Seller Dashboard Analytics',
    eyebrow: 'Performance overview',
    description:
      'A clean command center for revenue trend, order volume, category movement, and daily business checks. This is the first place a seller sees what is working today and what needs action.',
    bullets: [
      'Track totals and today-level performance in one glance',
      'Follow order movement and category sales with date filters',
      'Use the dashboard as the daily operating home screen',
    ],
    imagePath: '/landing_page/Seller-Dashboard-Analytics.png',
  },
  {
    title: 'Easy Order Management',
    eyebrow: 'Operational flow',
    description:
      'Order handling stays simple, with clear status updates, dispatch steps, and a workflow that helps teams move from pending to completed without confusion.',
    bullets: [
      'Keep order status updates and fulfillment steps visible',
      'Reduce back-and-forth with one clear seller workflow',
      'Support fast daily dispatch routines for active stores',
    ],
    imagePath: '/landing_page/Easy-Order-Management.png',
  },
  {
    title: 'Premium Logistics',
    eyebrow: 'Fulfillment confidence',
    description:
      'This story panel highlights reliable shipping flow, inventory movement, and delivery-ready operations for sellers that need structure as volume grows.',
    bullets: [
      'Present a premium logistics experience to customers',
      'Keep stock and dispatch readiness aligned',
      'Show a more polished fulfillment journey on the landing page',
    ],
    imagePath: '/landing_page/Premium-Logistics.png',
  },
  {
    title: 'Hand To Hand Delivery',
    eyebrow: 'Last-mile touchpoint',
    description:
      'Use this visual to explain a direct delivery promise, handoff clarity, and a smooth customer experience from seller to buyer.',
    bullets: [
      'Communicate direct and dependable delivery steps',
      'Make the customer handoff feel intentional and safe',
      'Support a simple story around trusted seller fulfillment',
    ],
    imagePath: '/landing_page/Hand-To-Hand-Delivery.png',
  },
  {
    title: 'Rich Support 24x7',
    eyebrow: 'Seller assistance',
    description:
      'This panel brings support, issue handling, and ticket visibility into the same story so the page feels complete and enterprise-ready.',
    bullets: [
      'Highlight responsive support and ticket clarity',
      'Reassure sellers with always-available help messaging',
      'Close the landing story with trust and reliability',
    ],
    imagePath: '/landing_page/Rich-Support-24x7.png',
  },
]

const publicAsset = (path: string) => encodeURI(path)

const heroPreviewItems = [
  {
    title: 'Seller dashboard',
    value: 'Revenue and orders at a glance',
    imagePath: storyPanels[0].imagePath,
  },
  {
    title: 'Support and trust',
    value: 'Fast help, clear operations',
    imagePath: storyPanels[4].imagePath,
  },
  {
    title: 'Plan controls',
    value: 'Billing and access built in',
    imagePath: storyPanels[1].imagePath,
  },
]

const SellerLandingPage = ({ initialSection }: SellerLandingPageProps) => {
  const pricingRef = useRef<HTMLElement | null>(null)
  const [scrolled, setScrolled] = useState(false)
  const [scrollProgress, setScrollProgress] = useState(0)
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40)
      const docEl = document.documentElement
      const scrollTop = window.scrollY
      const docHeight = docEl.scrollHeight - docEl.clientHeight
      setScrollProgress(docHeight > 0 ? Math.min((scrollTop / docHeight) * 100, 100) : 0)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    if (initialSection !== 'pricing') {
      return
    }

    pricingRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }, [initialSection])

  useEffect(() => {
    // Reveal-on-scroll using IntersectionObserver with rAF for smoothness
    if (typeof window === 'undefined') return

    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.querySelectorAll('.reveal').forEach((el) => el.classList.add('reveal--visible'))
      return
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            window.requestAnimationFrame(() => {
              ;(entry.target as HTMLElement).classList.add('reveal--visible')
            })
            io.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
    )

    document.querySelectorAll('.reveal').forEach((el) => io.observe(el))
    return () => io.disconnect()
  }, [])

  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') {
      return
    }

    const sellerUrl = new URL('/seller', window.location.origin).href
    const title = 'Trendify Seller | Seller Dashboard, Orders, Inventory & Payouts'
    const description =
      'Trendify Seller combines seller onboarding, analytics, product and inventory management, order operations, staff permissions, support tickets, billing, and wallet payouts in one workspace.'
    const ogImage = new URL(publicAsset('/landing_page/Seller-Dashboard-Analytics.png'), window.location.origin).href
    const keywordContent =
      'Trendify Seller, seller dashboard, seller landing page, inventory management, order management, Razorpay billing, wallet payouts, seller support, role based access, seller analytics, ecommerce seller platform'

    const managedNodes: HTMLElement[] = []

    const ensureMeta = (attribute: 'name' | 'property', key: string, content: string) => {
      let node = document.head.querySelector(`meta[${attribute}="${key}"]`) as HTMLMetaElement | null

      if (!node) {
        node = document.createElement('meta')
        node.setAttribute(attribute, key)
        node.dataset.sellerSeo = 'landing'
        document.head.appendChild(node)
        managedNodes.push(node)
      }

      node.setAttribute('content', content)
    }

    const ensureLink = (rel: string, href: string) => {
      let node = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null

      if (!node) {
        node = document.createElement('link')
        node.rel = rel
        node.dataset.sellerSeo = 'landing'
        document.head.appendChild(node)
        managedNodes.push(node)
      }

      node.href = href
    }

    const ensureScript = (id: string, payload: object) => {
      let node = document.head.querySelector(`script[data-seller-seo-id="${id}"]`) as HTMLScriptElement | null

      if (!node) {
        node = document.createElement('script')
        node.type = 'application/ld+json'
        node.dataset.sellerSeo = 'landing'
        node.dataset.sellerSeoId = id
        document.head.appendChild(node)
        managedNodes.push(node)
      }

      node.textContent = JSON.stringify(payload)
    }

    document.title = title
    ensureMeta('name', 'description', description)
    ensureMeta('name', 'keywords', keywordContent)
    ensureMeta('name', 'author', 'Trendify')
    ensureMeta('name', 'robots', 'index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1')
    ensureMeta('name', 'theme-color', '#0f766e')
    ensureMeta('name', 'application-name', 'Trendify Seller')
    ensureMeta('name', 'apple-mobile-web-app-title', 'Trendify Seller')
    ensureMeta('property', 'og:site_name', 'Trendify Seller')
    ensureMeta('property', 'og:title', title)
    ensureMeta('property', 'og:description', description)
    ensureMeta('property', 'og:type', 'website')
    ensureMeta('property', 'og:url', sellerUrl)
    ensureMeta('property', 'og:image', ogImage)
    ensureMeta('property', 'og:image:alt', 'Trendify Seller dashboard preview')
    ensureMeta('property', 'og:locale', 'en_IN')
    ensureMeta('name', 'twitter:card', 'summary_large_image')
    ensureMeta('name', 'twitter:title', title)
    ensureMeta('name', 'twitter:description', description)
    ensureMeta('name', 'twitter:image', ogImage)
    ensureLink('canonical', sellerUrl)

    ensureScript('webpage', {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description,
      url: sellerUrl,
      inLanguage: 'en-IN',
      isPartOf: {
        '@type': 'WebSite',
        name: 'Trendify Seller',
        url: sellerUrl,
      },
      primaryImageOfPage: ogImage,
      about: {
        '@type': 'SoftwareApplication',
        name: 'Trendify Seller',
        applicationCategory: 'BusinessApplication',
        operatingSystem: 'Web',
        url: sellerUrl,
        description,
      },
    })

    return () => {
      if (document.title === title) {
        document.title = 'Trendify'
      }

      managedNodes.forEach((node) => node.remove())
    }
  }, [])

  return (
    <div className="seller-landing">
      {/* Scroll progress bar */}
      <div
        className="seller-landing__scroll-progress"
        style={{ width: `${scrollProgress}%` }}
        aria-hidden="true"
      />

      <header className="seller-landing__hero">
        <div className="seller-landing__glow seller-landing__glow--left" />
        <div className="seller-landing__glow seller-landing__glow--right" />
        <div className="seller-landing__hero-grain" aria-hidden="true" />

        <div className="seller-landing__inner">
          <nav className={`seller-landing__topbar${scrolled ? ' seller-landing__topbar--sticky' : ''}`}>
            <Space size={10} align="center">
              <span className="seller-landing__brand-mark" aria-hidden="true">
                <ShopOutlined />
              </span>
              <div>
                <Typography.Title level={5} style={{ margin: 0, color: '#e8f6f3' }}>
                  Trendify Seller
                </Typography.Title>
                <Typography.Text style={{ color: '#9fd3c7', fontWeight: 700 }}>
                  Built for seller growth
                </Typography.Text>
              </div>
            </Space>

            <Space wrap>
              <Link to="/seller/login">
                <Button type="text" size="large" className="seller-landing__ghost-btn">
                  Seller Login
                </Button>
              </Link>
              <Link to="/seller/register">
                <Button type="primary" size="large" className="seller-landing__primary-btn">
                  Get Started
                </Button>
              </Link>
            </Space>
          </nav>

          <section className="seller-landing__hero-stack">
            <div>
              <Tag className="seller-landing__eyebrow" icon={<ThunderboltOutlined />}>
                Built on currently shipped seller modules
              </Tag>
              <Typography.Title className="seller-landing__hero-title reveal reveal--from-left">
                One seller workspace from onboarding approval to payouts
              </Typography.Title>
              <Typography.Paragraph className="seller-landing__hero-copy reveal reveal--from-left">
                Trendify Seller is built around the features already live in your stack: compliance-focused registration, admin approval, plan activation via Razorpay, dashboard analytics, product and inventory controls, order workflows, heatmap insights, role-based staff access, support ticketing, and wallet payouts.
              </Typography.Paragraph>

              <div className="seller-landing__hero-actions">
                <Space wrap size={14}>
                  <Link to="/seller/register">
                    <Button type="primary" size="large" className="seller-landing__primary-btn">
                      Create Seller Account <ArrowRightOutlined />
                    </Button>
                  </Link>
                  <Link to="/seller/login">
                    <Button size="large" className="seller-landing__secondary-btn">
                      Open Seller Dashboard
                    </Button>
                  </Link>
                </Space>
              </div>

              <div className="seller-landing__hero-points reveal reveal--from-left" aria-label="Seller trust points">
                {[
                  { text: 'Compliance-first onboarding and admin approval', sub: 'Verified seller registration flow' },
                  { text: 'Plan activation with Razorpay-backed billing', sub: 'Starter, Business, and Enterprise tiers' },
                  { text: 'Products, orders, support, wallet, and team access in one flow', sub: '10+ operational modules included' },
                ].map((item) => (
                  <div key={item.text} className="seller-landing__hero-point">
                    <CheckCircleFilled />
                    <div>
                      <div style={{ fontWeight: 600, color: '#e2f4f0' }}>{item.text}</div>
                      <div style={{ fontSize: '0.8rem', color: '#7fb3aa', marginTop: 2 }}>{item.sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Hero right — product screenshot */}
            <div className="seller-landing__hero-visual reveal reveal--from-right">
              <div className="seller-landing__hero-visual__browser">
                <div className="seller-landing__hero-visual__bar">
                  <span /><span /><span />
                  <div className="seller-landing__hero-visual__url">trendify.in/seller/dashboard</div>
                </div>
                <img
                  src={publicAsset('/landing_page/Seller-Dashboard-Analytics.png')}
                  alt="Trendify Seller Dashboard"
                  className="seller-landing__hero-visual__img"
                />
              </div>
              <div className="seller-landing__hero-visual__badges">
                <div className="seller-landing__hero-visual__badge">
                  <ShopOutlined style={{ color: '#34d399' }} />
                  <span>10+ Modules</span>
                </div>
                <div className="seller-landing__hero-visual__badge">
                  <CheckCircleFilled style={{ color: '#38bdf8' }} />
                  <span>Live &amp; Shipped</span>
                </div>
                <div className="seller-landing__hero-visual__badge">
                  <ThunderboltOutlined style={{ color: '#f59e0b' }} />
                  <span>Razorpay Ready</span>
                </div>
              </div>
            </div>
          </section>

          <section className="seller-landing__stats" aria-label="Seller value highlights">
            {heroStats.map((stat) => (
              <Card key={stat.label} className="seller-landing__stat-card reveal reveal--up reveal--pop" variant="borderless">
                <Typography.Text className="seller-landing__stat-label">{stat.label}</Typography.Text>
                <Typography.Title level={2} className="seller-landing__stat-value">
                  {stat.value}
                </Typography.Title>
                <Typography.Paragraph className="seller-landing__stat-hint">
                  {stat.hint}
                </Typography.Paragraph>
              </Card>
            ))}
          </section>
        </div>
      </header>

      <main className="seller-landing__main" id="seller-main-content">
        <section className="seller-landing__section">
          <div className="seller-landing__section-header">
            <span className="seller-landing__section-eyebrow seller-landing__section-eyebrow--red">Pain Points</span>
            <Typography.Title level={2} className="seller-landing__section-title">
              Problems we solve with existing product capabilities
            </Typography.Title>
            <Typography.Paragraph className="seller-landing__section-copy">
              Every point below maps to current auth flows, plan controls, API middleware, and seller dashboard pages — not placeholder promises.
            </Typography.Paragraph>
          </div>

          <div className="seller-landing__pain-grid">
            {painPoints.map((item, index) => (
              <div
                key={item.pain}
                className="seller-landing__pain-card reveal reveal--up reveal--pop"
                style={{ transitionDelay: `${index * 110}ms` }}
              >
                <div className="seller-landing__pain-card__problem">
                  <span className="seller-landing__pain-card__badge">Problem</span>
                  <p className="seller-landing__pain-card__pain-text">{item.pain}</p>
                </div>
                <div className="seller-landing__pain-card__solution">
                  <span className="seller-landing__pain-card__solution-label">✦ Solution</span>
                  <p className="seller-landing__pain-card__solution-text">{item.solution}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="seller-landing__section seller-landing__section--alt">
          <div className="seller-landing__section-header">
            <span className="seller-landing__section-eyebrow">Platform Features</span>
            <Typography.Title level={2} className="seller-landing__section-title">
              Implemented seller features across dashboard modules
            </Typography.Title>
          </div>

          <div className="seller-landing__feature-grid">
            {featureItems.map((feature, index) => (
              <div
                key={feature.title}
                className="seller-landing__feature-card reveal reveal--up reveal--pop"
                style={{ transitionDelay: `${index * 110}ms` }}
              >
                <div className="seller-landing__feature-card__top">
                  <span className="seller-landing__feature-icon">{feature.icon}</span>
                  <span className="seller-landing__feature-number">0{index + 1}</span>
                </div>
                <Typography.Title level={4} className="seller-landing__feature-title">
                  {feature.title}
                </Typography.Title>
                <Typography.Paragraph className="seller-landing__feature-copy">
                  {feature.description}
                </Typography.Paragraph>
                <div className="seller-landing__feature-card__footer">
                  <ArrowRightOutlined className="seller-landing__feature-arrow" />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section ref={pricingRef} className="seller-landing__section" id="seller-pricing">
          <Typography.Title level={2} className="seller-landing__section-title">
            Seller plans and pricing
          </Typography.Title>
          <div className="seller-landing__pricing-grid">
            {pricingPlans.map((plan, index) => (
              <Card
                key={plan.name}
                className={`seller-landing__pricing-card reveal reveal--up reveal--pop${selectedPlan === plan.code ? ' is-popular' : ''}`}
                style={{ transitionDelay: `${index * 110}ms`, cursor: 'pointer' }}
                variant="borderless"
                onClick={() => setSelectedPlan(plan.code)}
              >
                <div className="seller-landing__pricing-card__inner">
                  <div className="seller-landing__pricing-card__top">
                    {selectedPlan === plan.code ? <Tag className="seller-landing__popular-tag">Selected</Tag> : plan.popular ? <Tag className="seller-landing__popular-tag seller-landing__popular-tag--ghost">Most Popular</Tag> : null}
                    <Typography.Text className="seller-landing__plan-subtitle">{plan.subtitle}</Typography.Text>
                    <Typography.Title level={3} className="seller-landing__plan-name">
                      {plan.name}
                    </Typography.Title>
                    <Typography.Paragraph className="seller-landing__plan-price">
                      <>
                        <span className="seller-landing__currency">INR</span> {plan.monthlyPrice.toLocaleString('en-IN')}
                        <span className="seller-landing__cadence"> / month</span>
                      </>
                    </Typography.Paragraph>
                    <Typography.Paragraph className="seller-landing__plan-bestfor" style={{ marginTop: -6 }}>
                      Yearly: INR {plan.yearlyPrice.toLocaleString('en-IN')} / year
                    </Typography.Paragraph>
                    <Typography.Paragraph className="seller-landing__plan-bestfor">
                      {plan.bestFor}
                    </Typography.Paragraph>
                    <Typography.Paragraph className="seller-landing__plan-bestfor" style={{ marginTop: -8 }}>
                      {plan.inventoryLimit} · {plan.support} · {plan.payout}
                    </Typography.Paragraph>
                  </div>

                  <div className="seller-landing__pricing-card__highlights">
                    {plan.highlights.map((highlight) => (
                      <div key={highlight} className="seller-landing__check-row seller-landing__check-row--dark">
                        <CheckCircleFilled />
                        <span>{highlight}</span>
                      </div>
                    ))}
                  </div>

                  <div className="seller-landing__pricing-card__cta">
                    <Link to="/seller/register">
                      <Button block type={selectedPlan === plan.code ? 'primary' : 'default'} size="large" className="seller-landing__pricing-btn">
                        {plan.cta}
                      </Button>
                    </Link>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </section>

        {/* CTA Banner */}
        <section className="seller-landing__cta-banner reveal reveal--up reveal--pop">
          <div className="seller-landing__cta-banner__glow" aria-hidden="true" />
          <div className="seller-landing__cta-banner__content">
            <Tag className="seller-landing__eyebrow seller-landing__eyebrow--dark" icon={<ThunderboltOutlined />}>
              Start today — no credit card needed
            </Tag>
            <Typography.Title level={2} className="seller-landing__cta-title">
              Ready to run your store smarter?
            </Typography.Title>
            <Typography.Paragraph className="seller-landing__cta-copy">
              Join sellers already using Trendify to manage products, orders, staff, and payouts — all from one dashboard.
            </Typography.Paragraph>
            <Space wrap size={14} style={{ justifyContent: 'center' }}>
              <Link to="/seller/register">
                <Button type="primary" size="large" className="seller-landing__primary-btn seller-landing__primary-btn--xl">
                  Create Seller Account <ArrowRightOutlined />
                </Button>
              </Link>
              <Link to="/seller/login">
                <Button size="large" className="seller-landing__secondary-btn seller-landing__secondary-btn--dark">
                  Open Seller Dashboard
                </Button>
              </Link>
            </Space>
          </div>
        </section>

        <section className="seller-landing__section seller-landing__section--alt">
          <Typography.Title level={2} className="seller-landing__section-title">
            Designed for the full seller journey
          </Typography.Title>
          <Typography.Paragraph className="seller-landing__section-copy">
            Every section below maps to live seller modules, backend rules, and operational flows already shipped in Trendify.
          </Typography.Paragraph>

          <div className="seller-landing__story-stack">
            {storyPanels.map((panel, index) => (
              <article
                key={panel.title}
                className={`seller-landing__story-panel reveal reveal--pop ${index % 2 === 0 ? 'reveal--from-left' : 'reveal--from-right'}${index % 2 === 1 ? ' seller-landing__story-panel--reverse' : ''}`}
                style={{ transitionDelay: `${index * 120}ms` }}
              >
                <div className="seller-landing__story-media reveal reveal--up reveal--pop" style={{ transitionDelay: `${index * 120 + 80}ms` }}>
                  <img
                    className="seller-landing__story-image reveal reveal--up reveal--pop"
                    src={publicAsset(panel.imagePath)}
                    alt={panel.title}
                    loading="eager"
                  />
                </div>

                <div
                  className={`seller-landing__story-copy reveal reveal--pop ${index % 2 === 1 ? 'reveal--from-left' : 'reveal--from-right'}`}
                  style={{ transitionDelay: `${index * 120 + 120}ms` }}
                >
                  <Typography.Text className="seller-landing__story-eyebrow">{panel.eyebrow}</Typography.Text>
                  <Typography.Title level={3} className="seller-landing__story-title">
                    {panel.title}
                  </Typography.Title>
                  <Typography.Paragraph className="seller-landing__story-text">
                    {panel.description}
                  </Typography.Paragraph>

                  <ul className="seller-landing__story-bullets">
                    {panel.bullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>
      {/* Footer */}
      <footer className="seller-landing__footer">
        <div className="seller-landing__footer__inner">
          <div className="seller-landing__footer__brand">
            <Space size={10} align="center">
              <span className="seller-landing__brand-mark" aria-hidden="true">
                <ShopOutlined />
              </span>
              <div>
                <Typography.Title level={5} style={{ margin: 0, color: '#e8f6f3' }}>
                  Trendify Seller
                </Typography.Title>
                <Typography.Text style={{ color: '#9fd3c7', fontWeight: 700, fontSize: '0.72rem', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                  Built for seller growth
                </Typography.Text>
              </div>
            </Space>
            <Typography.Paragraph className="seller-landing__footer__tagline">
              One workspace from onboarding to payouts. Manage your store with confidence.
            </Typography.Paragraph>
            <Space size={12}>
              <a href="mailto:support@trendify.in" className="seller-landing__footer__social" aria-label="Email support">
                <MailOutlined />
              </a>
              <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="seller-landing__footer__social" aria-label="Twitter">
                <TwitterOutlined />
              </a>
              <a href="https://linkedin.com" target="_blank" rel="noopener noreferrer" className="seller-landing__footer__social" aria-label="LinkedIn">
                <LinkedinOutlined />
              </a>
              <a href="https://github.com" target="_blank" rel="noopener noreferrer" className="seller-landing__footer__social" aria-label="GitHub">
                <GithubOutlined />
              </a>
            </Space>
          </div>

          <div className="seller-landing__footer__links">
            <div className="seller-landing__footer__col">
              <Typography.Text className="seller-landing__footer__col-title">Platform</Typography.Text>
              <Link to="/seller/register" className="seller-landing__footer__link">Get Started</Link>
              <Link to="/seller/login" className="seller-landing__footer__link">Seller Login</Link>
              <a href="#seller-pricing" className="seller-landing__footer__link">Pricing</a>
              <a href="#seller-main-content" className="seller-landing__footer__link">Features</a>
            </div>
            <div className="seller-landing__footer__col">
              <Typography.Text className="seller-landing__footer__col-title">Seller Tools</Typography.Text>
              <span className="seller-landing__footer__link seller-landing__footer__link--muted">Dashboard Analytics</span>
              <span className="seller-landing__footer__link seller-landing__footer__link--muted">Inventory & Orders</span>
              <span className="seller-landing__footer__link seller-landing__footer__link--muted">Staff & Roles</span>
              <span className="seller-landing__footer__link seller-landing__footer__link--muted">Wallet & Payouts</span>
            </div>
            <div className="seller-landing__footer__col">
              <Typography.Text className="seller-landing__footer__col-title">Support</Typography.Text>
              <span className="seller-landing__footer__link seller-landing__footer__link--muted">Help Center</span>
              <span className="seller-landing__footer__link seller-landing__footer__link--muted">Ticket System</span>
              <a href="mailto:support@trendify.in" className="seller-landing__footer__link">Contact Us</a>
            </div>
          </div>
        </div>

        <div className="seller-landing__footer__bottom">
          <Typography.Text className="seller-landing__footer__copy">
            © {new Date().getFullYear()} Trendify Seller. All rights reserved.
          </Typography.Text>
          <Space size={16}>
            <span className="seller-landing__footer__legal">Privacy Policy</span>
            <span className="seller-landing__footer__legal">Terms of Service</span>
          </Space>
        </div>
      </footer>
    </div>
  )
}

export default SellerLandingPage
